import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { handleUpload } from "@utils";
import Logo from "@ui/Logo";
import CreateWorkspaceStep1 from "./CreateWorkspaceStep1";
import CreateWorkspaceStep2 from "./CreateWorkspaceStep2";

function CreateWorkspaceModal({ open, onOpenChange }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [name, setName] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [error, setError] = useState("");
    const [source, setSource] = useState("");
    const { call, isLoading, error: apiError } = useBluelagoon({ method: "createCompany" });

    const handleLogoChange = (file) => {
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const handleStep1Continue = () => {
        const trimmed = name.trim();
        if (!trimmed) { setError(t("sidebar.workspace.nameRequired")); return; }
        if (trimmed.length < 2) { setError(t("sidebar.workspace.nameTooShort")); return; }
        setError("");
        setStep(2);
    };

    const handleCreate = async () => {
        try {
            let logoUrl = null;
            if (logoFile) {
                const result = await handleUpload(logoFile, null, true);
                logoUrl = result.permanentUrl || result.url;
            }
            await call({ name: name.trim(), source, logoUrl });
            window.location.reload();
        } catch (err) {
            console.error("Error creating workspace:", err);
        }
    };

    const handleClose = () => {
        setStep(1);
        setName("");
        setLogoFile(null);
        if (logoPreview) URL.revokeObjectURL(logoPreview);
        setLogoPreview(null);
        setError("");
        setSource("");
        onOpenChange(false);
    };

    if (!open) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col">
            <div className="flex justify-center pt-8 pb-4">
                <Logo className="h-8" />
            </div>
            <div className="flex-1 flex items-center justify-center px-4 overflow-y-auto">
                <div className="w-full max-w-3xl bg-white rounded-2xl shadow-sm border border-gray-200 min-h-[480px] flex flex-col">
                    {step === 1 ? (
                        <CreateWorkspaceStep1
                            name={name} setName={setName} logoPreview={logoPreview}
                            onLogoChange={handleLogoChange} error={error} apiError={apiError}
                            setError={setError} onContinue={handleStep1Continue} onBack={handleClose}
                        />
                    ) : (
                        <CreateWorkspaceStep2
                            source={source} setSource={setSource} onContinue={handleCreate}
                            onSkip={handleCreate} onBack={() => setStep(1)} isLoading={isLoading}
                        />
                    )}
                </div>
            </div>
            <div className="py-4 text-center text-xs text-gray-400">
                {t("sidebar.workspace.footerText")}
            </div>
        </div>,
        document.body
    );
}

export default CreateWorkspaceModal;
